import React from 'react';

import { Text } from '@companycam/slab-web';
import { tokens } from '@companycam/slab-tokens';
import ShimmerLoader from 'components/shared/ShimmerLoader/ShimmerLoader';
import { formatDate } from 'helpers/intl-helper';
import styled from 'styled-components';
import useTrialEndQuery from '../choosePlans/gql/useTrialEndQuery';
import { ANNUAL } from '../Constants';
import { translate } from 'javascripts/translations/i18n';
import SubmitPaymentButton from './SubmitPaymentButton';
import { isToday } from 'date-fns';

type PlanPaymentInfoProps = {
  amountDue: number;
  billingLoading: boolean;
  handleSubmit: () => void;
  formId: string;
  isWalletPayment: boolean;
  paymentSubmitLoading: boolean;
  submitErrorMessage: string;
  selectedSubscriptionCycle: string;
  submitPaymentDisabled: boolean;
  planTitle: string;
};

// #region styled-components
const PlanInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  margin-left: var(--cc_size_spacing_xl);
  @media (max-width: 1000px) {
    margin-left: 0;
    margin-top: var(--cc_size_spacing_xl);
    padding-left: var(--cc_size_spacing_xs);
    padding-right: var(--cc_size_spacing_xs);
  }
  justify-content: space-between;
`;

const PlanPriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: var(--cc_size_spacing_l);
`;

const DueContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ButtonContainer = styled.div`
  margin-top: var(--cc_size_spacing_m);
`;
// #endregion styled-components

function PlanPaymentInfo({
  amountDue,
  billingLoading,
  handleSubmit,
  formId,
  isWalletPayment,
  paymentSubmitLoading,
  selectedSubscriptionCycle,
  submitErrorMessage,
  submitPaymentDisabled,
  planTitle,
}: PlanPaymentInfoProps) {
  const { trialEnd, loading: trialEndLoading } = useTrialEndQuery();
  let trialEndDate = null;
  trialEndDate = new Date(trialEnd);
  const trialEndsToday = isToday(trialEndDate);

  const isAnnual = selectedSubscriptionCycle === ANNUAL;
  const nextPayment = (amountDue / 100).toFixed(2);

  return (
    <PlanInfoContainer>
      <div>
        <div>
          <PlanPriceContainer>
            {billingLoading ? (
              <ShimmerLoader height={tokens.size_text_m} />
            ) : (
              <>
                <Text
                  as="p"
                  family="heading"
                  size="m"
                  weight={600}
                  data-testid="addPaymentModal__plan-title"
                >
                  {translate(
                    isAnnual
                      ? 'billing.stripePaymentModal.annualPlan'
                      : 'billing.stripePaymentModal.monthlyPlan',
                    { planTitle },
                  )}
                </Text>
                {trialEndsToday ? null : (
                  <Text
                    as="p"
                    family="heading"
                    size="m"
                    weight={600}
                    data-testid="addPaymentModal__plan-price"
                  >
                    ${nextPayment}
                  </Text>
                )}
              </>
            )}
          </PlanPriceContainer>
        </div>
        {billingLoading || trialEndLoading ? (
          <ShimmerLoader
            height={tokens.size_text_m}
            margin="0 0 var(--cc_size_spacing_xl) 0"
          />
        ) : (
          <>
            {trialEndsToday ? null : (
              <DueContainer>
                <Text
                  as="p"
                  family="heading"
                  size="m"
                  ccMargin="none none l none"
                  weight={600}
                  data-testid="addPaymentModal__payment-due-date"
                >
                  {translate('billing.stripePaymentModal.due')}{' '}
                  {formatDate(trialEndDate, 'MMMM d, yyyy')}
                </Text>
                <Text
                  as="p"
                  family="heading"
                  size="m"
                  weight={600}
                  data-testid="addPaymentModal__payment-due-price"
                >
                  ${nextPayment}
                </Text>
              </DueContainer>
            )}
            <DueContainer>
              <Text
                as="p"
                family="heading"
                size="m"
                ccMargin="none none l none"
                weight={600}
              >
                {translate('billing.stripePaymentModal.dueToday')}
              </Text>
              <Text as="p" family="heading" size="m" weight={600}>
                {trialEndsToday ? `$${nextPayment}` : '$0'}
              </Text>
            </DueContainer>
          </>
        )}
        <Text as="p" size="xxs" color="subtle" ccMargin="none none m none">
          {translate('billing.stripePaymentModal.afterTrialDisclaimer')}
        </Text>
      </div>
      <div>
        <Text
          as="p"
          family="heading"
          size="xs"
          weight={700}
          ccMargin="none none xxs none"
        >
          {translate('billing.stripePaymentModal.whenChargedInfo')}
        </Text>
        {trialEndsToday ? null : (
          <Text as="p" size="xs" ccMargin="none">
            {translate('billing.stripePaymentModal.reminderInfo')}
          </Text>
        )}
        <ButtonContainer>
          <SubmitPaymentButton
            handleSubmit={handleSubmit}
            formId={formId}
            submitErrorMessage={submitErrorMessage}
            submitPaymentDisabled={submitPaymentDisabled}
            cta={translate('billing.stripePaymentModal.addPaymentMethod')}
            dataContent="billing.stripePaymentModal.addPaymentMethod"
            isWalletPayment={isWalletPayment}
            paymentSubmitLoading={paymentSubmitLoading}
          />
        </ButtonContainer>
      </div>
    </PlanInfoContainer>
  );
}

export default PlanPaymentInfo;
